import {
  memberFromInfo,
  parseMessage,
  type ClientMessage,
  type Member,
  type Message,
  type TransportInfo,
} from './message';

/** What a transport reports for one channel, before any validation. */
export type TransportEvent =
  | { kind: 'publication'; data: unknown; info?: TransportInfo }
  | { kind: 'join'; info: TransportInfo }
  | { kind: 'leave'; info: TransportInfo }
  | { kind: 'subscribed'; wasRecovering: boolean; recovered: boolean };

/** A validated channel event. `from` is null for server-published messages. */
export type RealtimeEvent =
  | { kind: 'message'; message: Message; from: Member | null }
  | { kind: 'join'; member: Member }
  | { kind: 'leave'; member: Member }
  | { kind: 'subscribed'; wasRecovering: boolean; recovered: boolean };

/** The seam between the socket and the app. Tests plug in an in-memory one. */
export interface RealtimeTransport {
  /** Listens to a channel, subscribing to it if nobody else is. Returns an unsubscribe function. */
  subscribe(channel: string, listener: (event: TransportEvent) => void): () => void;
  /** Fire-and-forget: these messages are ephemeral, so a failed send is not retried. */
  publish(channel: string, message: ClientMessage): void;
  presence(channel: string): Promise<Member[]>;
  /**
   * This connection's id, null until connected. It changes on every reconnect. Peers are told
   * apart from ourselves by it, not by user id, so the same person's other tabs still show up.
   */
  clientId(): string | null;
}

export interface RealtimeConnection extends RealtimeTransport {
  start(): void;
  stop(): void;
}

function toMessage(event: Extract<TransportEvent, { kind: 'publication' }>): RealtimeEvent | null {
  const message = parseMessage(event.data);
  if (message === null) return null;
  // Client messages are only meaningful with a known sender; server ones have none.
  const from = memberFromInfo(event.info);
  if (message.type !== 'entity.changed' && from === null) return null;
  return { kind: 'message', message, from };
}

/** Drops anything malformed or from an unidentifiable sender. */
export function toRealtimeEvent(event: TransportEvent): RealtimeEvent | null {
  switch (event.kind) {
    case 'publication':
      return toMessage(event);
    case 'join':
    case 'leave': {
      const member = memberFromInfo(event.info);
      return member === null ? null : { kind: event.kind, member };
    }
    case 'subscribed':
      return event;
  }
}

/** What a mounted realtime connection gives its consumers. */
export type ActiveRealtime = {
  transport: RealtimeTransport;
  environmentId: number;
  /** This browser tab's id (see `tab-id.ts`); keys this tab's own color. */
  tab: string;
};
